import React, { useState, useEffect } from 'react';
import { 
  userManagementService, 
  AdminUser, 
  Role, 
  Permission,
  CreateUserDto 
} from '../services/userManagementService';
import { useAuth } from '../contexts/AuthContext';

type ModalType = 'create' | 'edit' | 'permissions' | 'view' | null;
type ViewMode = 'table' | 'cards';

// SVG Icon Components
const UsersGroupIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const TableIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const GridIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const WarningIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

// Role-specific icons
const CrownIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l3.5 7L12 6l3.5 4L19 3M5 21h14M5 17h14M5 13h14" />
  </svg>
);

const ChartIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const CarIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);

const CalendarIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const AnalyticsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const PencilIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const CogIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// Role icon component
const RoleIcon = ({ role, className = "w-5 h-5" }: { role: string; className?: string }) => {
  const icons: Record<string, React.FC<{ className?: string }>> = {
    admin: CrownIcon,
    seo_manager: ChartIcon,
    fleet_manager: CarIcon,
    booking_manager: CalendarIcon,
    analytics_viewer: AnalyticsIcon,
    content_editor: PencilIcon,
    custom: CogIcon,
  };
  const IconComponent = icons[role] || CogIcon;
  return <IconComponent className={className} />;
};

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<CreateUserDto>({
    name: '', email: '', password: '', role: 'custom', permissions: [], phone: ''
  });
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData, permsData] = await Promise.all([
        userManagementService.getUsers(),
        userManagementService.getRoles(),
        userManagementService.getPermissions()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setPermissions(permsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (modalType === 'create' && !formData.password) errors.password = 'Password is required';
    if (formData.password && formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCreateModal = () => {
    setFormData({ name: '', email: '', password: '', role: 'custom', permissions: [], phone: '' });
    setSelectedPermissions([]);
    setFormErrors({});
    setModalType('create');
  };

  const openEditModal = (user: AdminUser) => {
    setSelectedUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role, permissions: user.permissions, phone: user.phone || '' });
    setSelectedPermissions(user.permissions);
    setFormErrors({});
    setModalType('edit');
  };

  const openViewModal = (user: AdminUser) => { setSelectedUser(user); setModalType('view'); };
  const openPermissionsModal = (user: AdminUser) => { setSelectedUser(user); setSelectedPermissions(user.permissions); setModalType('permissions'); };

  const handleRoleChange = (role: string) => {
    setFormData({ ...formData, role });
    const selectedRole = roles.find(r => r.id === role);
    if (selectedRole && role !== 'custom') setSelectedPermissions(selectedRole.permissions);
  };

  const togglePermission = (permId: string) => {
    setSelectedPermissions(prev => prev.includes(permId) ? prev.filter(p => p !== permId) : [...prev, permId]);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (modalType === 'create') {
        const newUser = await userManagementService.createUser({ ...formData, permissions: selectedPermissions });
        if (newUser) { setUsers([newUser, ...users]); setModalType(null); }
      } else if (modalType === 'edit' && selectedUser) {
        const updated = await userManagementService.updateUser(selectedUser._id, { ...formData, permissions: selectedPermissions, password: formData.password || undefined });
        if (updated) { setUsers(users.map(u => u._id === updated._id ? updated : u)); setModalType(null); }
      } else if (modalType === 'permissions' && selectedUser) {
        const updated = await userManagementService.updatePermissions(selectedUser._id, selectedPermissions);
        if (updated) { setUsers(users.map(u => u._id === updated._id ? updated : u)); setModalType(null); }
      }
    } catch (error) { console.error('Error saving user:', error); alert('Error saving user'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (user: AdminUser) => {
    const updated = await userManagementService.toggleActive(user._id);
    if (updated) setUsers(users.map(u => u._id === updated._id ? updated : u));
  };

  const handleDelete = async (user: AdminUser) => {
    if (!confirm(`Are you sure you want to delete ${user.name}?`)) return;
    const success = await userManagementService.deleteUser(user._id);
    if (success) setUsers(users.filter(u => u._id !== user._id));
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || (filterStatus === 'active' && user.isActive) || (filterStatus === 'inactive' && !user.isActive);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  const stats = { total: users.length, active: users.filter(u => u.isActive).length, inactive: users.filter(u => !u.isActive).length, admins: users.filter(u => u.role === 'admin').length };

  const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const getAvatarGradient = (name: string) => {
    const gradients = ['from-blue-500 to-indigo-600', 'from-purple-500 to-pink-600', 'from-green-500 to-teal-600', 'from-orange-500 to-red-600', 'from-cyan-500 to-blue-600', 'from-rose-500 to-purple-600'];
    return gradients[name.charCodeAt(0) % gradients.length];
  };
  const getRoleColorClasses = (role: string) => {
    const colors: Record<string, string> = { admin: 'bg-purple-100 text-purple-700', seo_manager: 'bg-blue-100 text-blue-700', fleet_manager: 'bg-green-100 text-green-700', booking_manager: 'bg-orange-100 text-orange-700', analytics_viewer: 'bg-cyan-100 text-cyan-700', content_editor: 'bg-pink-100 text-pink-700', custom: 'bg-gray-100 text-gray-700' };
    return colors[role] || colors.custom;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-500 font-medium">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <UsersGroupIcon />
            </div>
            <div>
              <h1 className="text-2xl font-bold">User Management</h1>
              <p className="text-blue-100 text-sm">Manage team members and their access permissions</p>
            </div>
          </div>
          <button onClick={openCreateModal} className="px-5 py-2.5 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 self-start lg:self-auto">
            <PlusIcon /> Add New User
          </button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1"><UsersGroupIcon /><span className="text-blue-100 text-sm">Total Users</span></div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span className="text-blue-100 text-sm">Active</span>
            </div>
            <div className="text-2xl font-bold">{stats.active}</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span className="text-blue-100 text-sm">Inactive</span>
            </div>
            <div className="text-2xl font-bold">{stats.inactive}</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1"><CrownIcon className="w-5 h-5" /><span className="text-blue-100 text-sm">Admins</span></div>
            <div className="text-2xl font-bold">{stats.admins}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><SearchIcon /></div>
            <input type="text" placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
          </div>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white min-w-[160px]">
            <option value="all">All Roles</option>
            {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} className="px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white min-w-[140px]">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button onClick={() => setViewMode('table')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'table' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}><TableIcon /></button>
            <button onClick={() => setViewMode('cards')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'cards' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}><GridIcon /></button>
          </div>
        </div>
      </div>

      {/* Role Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {roles.map(role => {
          const count = users.filter(u => u.role === role.id).length;
          const isSelected = filterRole === role.id;
          return (
            <div key={role.id} className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-[1.02] ${isSelected ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow'}`}
              onClick={() => setFilterRole(filterRole === role.id ? 'all' : role.id)}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${getRoleColorClasses(role.id)}`}>
                <RoleIcon role={role.id} className="w-5 h-5" />
              </div>
              <div className="text-sm font-semibold text-gray-900 truncate">{role.name}</div>
              <div className="text-xs text-gray-500 mt-1">{count} user{count !== 1 ? 's' : ''}</div>
              {isSelected && <div className="mt-2 flex items-center gap-1 text-xs text-blue-600"><CheckIcon /> Filtered</div>}
            </div>
          );
        })}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>Showing {filteredUsers.length} of {users.length} users</span>
        {(searchTerm || filterRole !== 'all' || filterStatus !== 'all') && (
          <button onClick={() => { setSearchTerm(''); setFilterRole('all'); setFilterStatus('all'); }} className="text-blue-600 hover:text-blue-800 font-medium">Clear filters</button>
        )}
      </div>

      {/* Card View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map(user => (
            <div key={user._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200">
              <div className={`h-2 bg-gradient-to-r ${getAvatarGradient(user.name)}`}></div>
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getAvatarGradient(user.name)} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>{getInitials(user.name)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{user.name}</h3>
                      {user._id === currentUser?.id && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">You</span>}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${getRoleColorClasses(user.role)}`}>
                        <RoleIcon role={user.role} className="w-3.5 h-3.5" /> {roles.find(r => r.id === user.role)?.name}
                      </span>
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {user.isActive ? '● Active' : '○ Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <span>{user.permissions.length} permissions</span>
                    <span>Last login: {formatDate(user.lastLogin)}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openViewModal(user)} className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">View</button>
                    <button onClick={() => openEditModal(user)} className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors">Edit</button>
                    <button onClick={() => handleDelete(user)} className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"><TrashIcon /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Permissions</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Login</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map(user => (
                  <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getAvatarGradient(user.name)} flex items-center justify-center text-white font-semibold text-sm shadow`}>{getInitials(user.name)}</div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{user.name}</span>
                            {user._id === currentUser?.id && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded font-medium">YOU</span>}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${getRoleColorClasses(user.role)}`}>
                        <RoleIcon role={user.role} className="w-4 h-4" />
                        {roles.find(r => r.id === user.role)?.name || user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button onClick={() => openPermissionsModal(user)} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group">
                        <span className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-xs font-bold group-hover:bg-blue-200 transition-colors">{user.permissions.length}</span>
                        <span>permissions</span>
                        <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleToggleActive(user)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-105 ${user.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
                        <span className={`w-2 h-2 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4"><div className="text-sm text-gray-600">{formatDate(user.lastLogin)}</div></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openViewModal(user)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="View"><EyeIcon /></button>
                        <button onClick={() => openEditModal(user)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all" title="Edit"><EditIcon /></button>
                        <button onClick={() => handleDelete(user)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Delete"><TrashIcon /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><UsersGroupIcon /></div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No users found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      )}

      {/* View User Modal */}
      {modalType === 'view' && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp">
            <div className={`h-24 bg-gradient-to-r ${getAvatarGradient(selectedUser.name)} relative`}>
              <button onClick={() => setModalType(null)} className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center text-white hover:bg-white/30 transition-colors"><XIcon /></button>
            </div>
            <div className="px-6 pb-6">
              <div className="flex items-end gap-4 -mt-10 mb-4">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${getAvatarGradient(selectedUser.name)} flex items-center justify-center text-white font-bold text-2xl shadow-xl border-4 border-white`}>{getInitials(selectedUser.name)}</div>
                <div className="pb-1">
                  <h2 className="text-xl font-bold text-gray-900">{selectedUser.name}</h2>
                  <p className="text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${getRoleColorClasses(selectedUser.role)}`}>
                    <RoleIcon role={selectedUser.role} className="w-4 h-4" /> {roles.find(r => r.id === selectedUser.role)?.name}
                  </span>
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${selectedUser.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{selectedUser.isActive ? '● Active' : '○ Inactive'}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                  <div><div className="text-xs text-gray-500 mb-1">Phone</div><div className="font-medium text-gray-900">{selectedUser.phone || 'Not set'}</div></div>
                  <div><div className="text-xs text-gray-500 mb-1">Last Login</div><div className="font-medium text-gray-900">{formatDate(selectedUser.lastLogin)}</div></div>
                  <div><div className="text-xs text-gray-500 mb-1">Created</div><div className="font-medium text-gray-900">{formatDate(selectedUser.createdAt)}</div></div>
                  <div><div className="text-xs text-gray-500 mb-1">Permissions</div><div className="font-medium text-gray-900">{selectedUser.permissions.length} granted</div></div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Permissions</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.permissions.map(perm => <span key={perm} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg">{permissions.find(p => p.id === perm)?.name || perm}</span>)}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setModalType(null); openEditModal(selectedUser); }} className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors">Edit User</button>
                <button onClick={() => setModalType(null)} className="px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(modalType === 'create' || modalType === 'edit') && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-slideUp">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                  {modalType === 'create' ? <PlusIcon /> : <EditIcon />}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{modalType === 'create' ? 'Add New User' : 'Edit User'}</h2>
                  <p className="text-blue-100 text-sm">{modalType === 'create' ? 'Create a new team member account' : 'Update user information and permissions'}</p>
                </div>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${formErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} placeholder="John Doe" />
                    {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${formErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} placeholder="john@example.com" />
                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password {modalType === 'edit' && <span className="text-gray-400 font-normal">(leave blank to keep)</span>}</label>
                    <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${formErrors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} placeholder="••••••••" />
                    {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all" placeholder="+61 400 000 000" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Select Role</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {roles.map(role => (
                      <button key={role.id} type="button" onClick={() => handleRoleChange(role.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-200 hover:scale-[1.02] ${formData.role === role.id ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${getRoleColorClasses(role.id)}`}><RoleIcon role={role.id} className="w-4 h-4" /></div>
                        <div className="text-sm font-semibold text-gray-900">{role.name}</div>
                        <div className="text-xs text-gray-500 mt-1">{role.permissions.length} perms</div>
                        {formData.role === role.id && <div className="mt-2 flex items-center gap-1 text-xs text-blue-600"><CheckIcon /> Selected</div>}
                      </button>
                    ))}
                  </div>
                </div>
                {formData.role === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Custom Permissions</label>
                    <div className="border border-gray-200 rounded-xl p-4 max-h-64 overflow-y-auto bg-gray-50">
                      {Object.entries(permissionsByCategory).map(([category, perms]) => (
                        <div key={category} className="mb-5 last:mb-0">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><span className="w-2 h-2 bg-blue-500 rounded-full"></span>{category}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {perms.map(perm => (
                              <label key={perm.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedPermissions.includes(perm.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
                                <input type="checkbox" checked={selectedPermissions.includes(perm.id)} onChange={() => togglePermission(perm.id)} className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500" />
                                <span className="text-sm text-gray-700">{perm.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{selectedPermissions.length} permissions selected</p>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setModalType(null)} className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-colors">Cancel</button>
              <button onClick={handleSubmit} disabled={saving} className="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                {saving && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                {modalType === 'create' ? 'Create User' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {modalType === 'permissions' && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-slideUp">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center"><LockIcon /></div>
                <div>
                  <h2 className="text-xl font-bold">Manage Permissions</h2>
                  <p className="text-purple-100 text-sm">{selectedUser.name} • {roles.find(r => r.id === selectedUser.role)?.name}</p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-amber-50 border-b border-amber-200">
              <div className="flex items-center gap-2 text-amber-800 text-sm"><WarningIcon /><span>Changing permissions will set the user's role to "Custom"</span></div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
              {Object.entries(permissionsByCategory).map(([category, perms]) => (
                <div key={category} className="mb-6 last:mb-0">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2 sticky top-0 bg-white py-2">
                    <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center"><span className="w-2 h-2 bg-purple-500 rounded-full"></span></span>
                    {category}
                    <span className="text-xs text-gray-400 font-normal ml-auto">{perms.filter(p => selectedPermissions.includes(p.id)).length}/{perms.length} selected</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {perms.map(perm => (
                      <label key={perm.id} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-[1.01] ${selectedPermissions.includes(perm.id) ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-gray-200 hover:border-purple-300'}`}>
                        <input type="checkbox" checked={selectedPermissions.includes(perm.id)} onChange={() => togglePermission(perm.id)} className="mt-0.5 w-5 h-5 rounded text-purple-600 focus:ring-purple-500" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{perm.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{perm.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-700 font-bold">{selectedPermissions.length}</span>
                <span className="text-gray-600">permissions selected</span>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setModalType(null)} className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-colors">Cancel</button>
                <button onClick={handleSubmit} disabled={saving} className="px-5 py-2.5 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {saving && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                  Save Permissions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
